import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Image,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useQueryWrapper } from "services/api/apiHelper";
import useGlobalStore from "app/store";
import { useParams, useSearchParams } from "react-router-dom";
import { useFormPrepopulationWithReset, ozeGhanaFieldMappings } from "hooks/useFormPrepopulation";
import { convertParamsToString, extractFieldNames } from "../../utils/helpers";
import { applyOptions, request } from "../../services/api/endpoints";
import WithSuspense from "../../components/HOC/withSuspense";
import { lazy, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useLoanApplication from "../../hooks/useLoanApplication";
import { toast } from "react-hot-toast";
import LanguageSelect from "../../components/General/LanguageSelect";
import { FormProvider } from "react-hook-form";
import { BsArrowRight, BsChevronLeft, BsChevronRight } from "react-icons/bs";
import Logo from "../../assets/images/green_logo.svg";
import {
  additionalFields,
  bankInfoFields,
  businessDetailsField1,
  businessDetailsField2,
  businessRegField,
  financialDetailsField,
  housingFields,
  nextOfKinFields,
  personalFields1,
  personalFields2,
} from "../../components/LoanApplication/fields";
import { QKEYS } from "../../utils/constants";
// import { HiX } from "react-icons/hi";
import LoanApplicationModal from "../../components/General/LoanModal";
import BuildAfricaForm from "pages/Apply/BuildAfricaForm";

// Dynamic import for MobileStepper to resolve TypeScript issues
const MobileStepper = WithSuspense(lazy(() => import("components/General/MobileStepper")));

const PersonalInformation = WithSuspense(
  lazy(() => import("components/LoanApplication/PersonalInformation")),
);
const BusinessInformation = WithSuspense(
  lazy(() => import("components/LoanApplication/BusinessInformation")),
);
const FinancialInformation = WithSuspense(
  lazy(() => import("components/LoanApplication/FinancialInformation")),
);
const LoanInformation = WithSuspense(
  lazy(() => import("components/LoanApplication/LoanInformation")),
);
const BankInformation = WithSuspense(
  lazy(() => import("components/LoanApplication/BankInformation")),
);

const DrugstocForm = WithSuspense(lazy(() => import("pages/Apply/DrugstocForm")));
const housingSituation = localStorage.getItem("housingSituationOptions");
const cellphoneUsageOptions = localStorage.getItem("cellphoneUsageOptions");
const loanReasonOptions = sessionStorage.getItem("loanReasonOptions");

export default function LoanFormPage() {
  const { alias } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  // Check if we should show mobile stepper
  const isMobile = useBreakpointValue({ base: true, md: false });

  const ngPages = [
    t("personalInformation"),
    t("businessInformation"),
    t("financialInformation"),
    t("loanInformation"),
    t("bankInformation"),
  ];

  const ghPages = [
    t("personalInformation"),
    t("businessInformation"),
    t("financialInformation"),
    t("loanInformation"),
  ];

  // Extract field names for each tab
  const personalFields = [
    ...personalFields1(t),
    ...personalFields2,
    ...housingFields(t),
    ...additionalFields,
    ...nextOfKinFields,
  ];

  const businessFields = [...businessDetailsField1, ...businessDetailsField2, ...businessRegField];

  const personalFieldNames = extractFieldNames(personalFields);
  const businessFieldNames = extractFieldNames(businessFields);
  const financialDetailsFieldFieldNames = extractFieldNames(financialDetailsField);
  const loanFieldNames = ["amount", "reasons"];

  const [setAcquisitionPartner, setCustomization, acquisitionPartner] = useGlobalStore(
    ({ setAcquisitionPartner, setCustomization, acquisitionPartner }) => [
      setAcquisitionPartner,
      setCustomization,
      acquisitionPartner,
    ],
  );

  const [productID, setProductID] = useState();

  const loanCurrency = acquisitionPartner?.currency ?? "NGN";

  const pages = loanCurrency === "NGN" ? ngPages : ghPages;

  const tabFields = {
    0: [...personalFieldNames, "nextOfKinPhone", "phone"],
    1: [...businessFieldNames],
    2: [...financialDetailsFieldFieldNames, "hasOtherBusiness"],
    3: [...loanFieldNames],
    ...(pages.length === 5 && { 4: bankInfoFields }),
  };

  const [isDraft, setIsDraft] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const pageTop = useRef<HTMLDivElement>(null);

  const [methods, submitForm, isLoading] = useLoanApplication();

  // Prepopulate form from URL query parameters using declarative configuration
  useFormPrepopulationWithReset(searchParams, methods.reset, ozeGhanaFieldMappings);

  const onInvalid = () => {
    toast.error(t("fillAllRequiredFields"), { position: "top-center" });
  };

  const onFormSubmit = (data: any, isDraft: boolean) => {
    console.log("Form Errors:", methods.formState.errors);
    console.log("Form Errors-valid:", methods.formState.isValid);
    console.log(data);
    setIsDraft(isDraft);
    if (tabIndex === pages.length - 1) {
      // if (methods.formState.isValid) {
      submitForm(data, isDraft);
      // } else {
      //   onInvalid();
      // }
    }
  };

  useEffect(() => {
    // Log all errors in the form whenever there is a change in the errors object
    if (Object.keys(methods.formState.errors).length > 0) {
      console.log("Form Errors:", methods.formState.errors);
    }
  }, [methods.formState.errors]);

  const updatePosition = async (dest: string) => {
    let initialPosition = tabIndex;
    if (dest === "next" && tabIndex < pages.length - 1) {
      const fieldsToValidate = tabFields[tabIndex];
      const isValid = await methods.trigger(fieldsToValidate);
      if (isValid) {
        initialPosition++;
        onFormSubmit(methods.getValues(), true);
      } else {
        onInvalid();
        return;
      }
    }
    if (dest === "prev" && tabIndex > 0) {
      initialPosition--;
    }
    setTabIndex(initialPosition);
    pageTop.current?.scrollIntoView();
  };

  const handleTabsChange = async (index: SetStateAction<number>) => {
    setTabIndex(index);
  };

  const handleFormSubmit = methods.handleSubmit((data) => onFormSubmit(data, false), onInvalid);

  const handleErrorToast = (error?: any) => {
    console.log("error:", error);
    setShowModal(true);
    // toast.error(
    //   error
    //     ? `${error.response?.data?.message}`
    //     : "Oop! An error occurred. Please try again later.",
    //   { position: "top-center" },
    // );
  };

  useQueryWrapper([QKEYS.HOUSING_SITUATION], applyOptions.HOUSING_SITUATION, {
    onSuccess: (data) => {
      localStorage.setItem("housingSituationOptions", JSON.stringify(data));
    },
    enabled: !housingSituation?.length,
  });
  //
  useQueryWrapper([QKEYS.PERIOD_USING_CELLPHONE], applyOptions.PERIOD_USING_CELLPHONE, {
    onSuccess: (data) => {
      localStorage.setItem("cellphoneUsageOptions", JSON.stringify(data));
    },
    enabled: !cellphoneUsageOptions?.length,
  });

  useQueryWrapper([QKEYS.REASONS_FOR_LOAN], applyOptions.REASONS_FOR_LOAN, {
    onSuccess: (data) => {
      sessionStorage.setItem("loanReasonOptions", JSON.stringify(data));
    },
    enabled: !loanReasonOptions?.length,
  });

  // Authorize borrower using the forms sessionID
  const { isLoading: isLoadingAcquisitionPartner, error: acquisitionPartnerError } =
    useQueryWrapper(
      [QKEYS.ACQUISITION_PARTNER],
      convertParamsToString(request.ACQUISITION_PARTNER, { alias }),
      {
        onSuccess: (data) => {
          setProductID(data.productID);
          setAcquisitionPartner(data);
          setCustomization(data);
        },
        onError: handleErrorToast,
      },
    );

  // todo Fetch state

  return (
    <>
      {alias === "drugstoc" && !isLoadingAcquisitionPartner && (
        <DrugstocForm productID={productID} />
      )}

      {alias === "buildafrica" && !isLoadingAcquisitionPartner && (
        <BuildAfricaForm productID={productID ?? ""} />
      )}
      

      {alias != "drugstoc" && alias != "buildafrica" && isLoadingAcquisitionPartner && (
        <Box>
          <Center height="75vh">
            <Image src={Logo} width="52" className="zoom_image" />
          </Center>
        </Box>
      )}


      {alias != "drugstoc" && alias !="buildafrica" && !isLoadingAcquisitionPartner && !acquisitionPartnerError ? (
        <>
          <Flex justifyContent="end" mb={{ base: 2, md: 4 }}>
            <LanguageSelect />
          </Flex>
          <Box p={{ base: "3", md: "5" }} shadow="md" ref={pageTop}>
            <Text
              color="primary"
              fontWeight="800"
              fontSize={{ base: "2xl", md: "3xl" }}
              textAlign="left"
            >
              {t("apply")}
            </Text>
            <Divider my={{ base: "3", md: "5" }} />
            <Box bg="white">
              {/* Mobile Stepper */}
              {isMobile && (
                <MobileStepper
                  currentStep={tabIndex + 1}
                  totalSteps={pages.length}
                  stepTitle={pages[tabIndex]}
                  stepNumber={tabIndex + 1}
                />
              )}

              <Tabs index={tabIndex} onChange={handleTabsChange}>
                {/* Desktop Tab List */}
                {!isMobile && (
                  <TabList
                    overflow={{ base: "scroll", lg: "visible" }}
                    pb={{ base: "4", lg: 0 }}
                    sx={{
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    {pages.map((page) => (
                      <Tab
                        key={page}
                        fontSize={{ base: "sm", md: "md" }}
                        px={{ base: 2, md: 4 }}
                        py={{ base: 2, md: 3 }}
                        whiteSpace="nowrap"
                      >
                        {page}
                      </Tab>
                    ))}
                  </TabList>
                )}
                <FormProvider {...methods}>
                  {/* <form onSubmit={methods.handleSubmit((data) => onFormSubmit(data, false), onInvalid)}>*/}
                  <form onSubmit={handleFormSubmit}>
                    <TabPanels>
                      <TabPanel px={{ base: 0, lg: 4 }}>
                        <PersonalInformation />
                      </TabPanel>
                      <TabPanel px={{ base: 0, lg: 4 }}>
                        <BusinessInformation />
                      </TabPanel>
                      <TabPanel px={{ base: 0, lg: 4 }}>
                        <FinancialInformation />
                      </TabPanel>
                      <TabPanel px={{ base: 0, lg: 4 }}>
                        <LoanInformation />
                      </TabPanel>
                      {loanCurrency === "NGN" && (
                        <TabPanel px={{ base: 0, lg: 4 }}>
                          <BankInformation />
                        </TabPanel>
                      )}
                    </TabPanels>
                  </form>
                </FormProvider>
              </Tabs>
            </Box>
          </Box>
          <Box my={{ base: "6", md: "10" }}>
            <Flex
              justifyContent="space-between"
              direction={{ base: "column", sm: "row" }}
              gap={{ base: 4, sm: 0 }}
              align={{ base: "stretch", sm: "center" }}
            >
              {tabIndex === 0 ? (
                <Box />
              ) : (
                <>
                  {/* Mobile Button */}
                  <Button
                    leftIcon={<BsChevronLeft />}
                    variant="outline"
                    size="md"
                    onClick={() => updatePosition("prev")}
                    width="full"
                    display={{ base: "flex", md: "none" }}
                  >
                    {pages[tabIndex - 1]}
                  </Button>
                  
                  {/* Desktop Text Link */}
                  <Flex
                    alignItems="center"
                    justifyContent="flex-start"
                    cursor="pointer"
                    _hover={{ textDecoration: "underline", textDecorationColor: "accent" }}
                    onClick={() => updatePosition("prev")}
                    minH="40px"
                    display={{ base: "none", md: "flex" }}
                  >
                    <Text fontWeight="200" fontSize={{ base: "lg", md: "xl" }} color="accent">
                      <BsChevronLeft />
                    </Text>
                    <Text
                      color="accent"
                      ml={{ base: "3", md: "5" }}
                      fontSize={{ base: "sm", md: "md" }}
                      noOfLines={1}
                    >
                      {pages[tabIndex - 1]}
                    </Text>
                  </Flex>
                </>
              )}
              {tabIndex === pages.length - 1 ? (
                <Button
                  px={{ base: "6", md: "8" }}
                  rightIcon={<BsArrowRight />}
                  size={{ base: "md", md: "md" }}
                  variant="solid"
                  isLoading={isLoading && !isDraft}
                  colorScheme="green"
                  width={{ base: "full", sm: "auto" }}
                  onClick={methods.handleSubmit((data) => onFormSubmit(data, false), onInvalid)}
                >
                  {t("submitApplication")}
                </Button>
              ) : (
                <>
                  {/* Mobile Button */}
                  <Button
                    rightIcon={<BsChevronRight />}
                    variant="solid"
                    colorScheme="blue"
                    size="md"
                    onClick={() => updatePosition("next")}
                    width="full"
                    display={{ base: "flex", md: "none" }}
                  >
                    {pages[tabIndex + 1]}
                  </Button>
                  
                  {/* Desktop Text Link */}
                  <Flex
                    alignItems="center"
                    justifyContent="flex-end"
                    _hover={{ textDecoration: "underline", textDecorationColor: "accent" }}
                    cursor="pointer"
                    onClick={() => updatePosition("next")}
                    minH="40px"
                    display={{ base: "none", md: "flex" }}
                  >
                    <Text
                      color="accent"
                      mr={{ base: "3", md: "5" }}
                      fontSize={{ base: "sm", md: "md" }}
                      noOfLines={1}
                    >
                      {pages[tabIndex + 1]}
                    </Text>
                    <Text fontWeight="200" fontSize={{ base: "lg", md: "xl" }} color="accent">
                      <BsChevronRight />
                    </Text>
                  </Flex>
                </>
              )}
            </Flex>
          </Box>
        </>
      ) : (
        alias != "drugstoc" && alias !="buildafrica" && (
          <Box>
            <Center height="75vh">
              <Image src={Logo} width="52" className="zoom_image" />
            </Center>
          </Box>
        )
      )}

      <LoanApplicationModal
        isOpen={showModal}
        onClose={() => console.log("")}
        size="md"
        title={"Page not found"}
      >
        <Center>
          <Text fontSize="md">
            This link is invalid or no longer available. Please check the link and try again.
          </Text>
        </Center>
      </LoanApplicationModal>
    </>
  );
}

/**
 *
 * This page handles all the API calls that needs to be made before the user gets to see the application forms page
 * note: we will be using the query keys as identifiers
 * How it works:
 * 1. Get sessionID and defaultCurrency from the url coming from the API
 * 2. Use the sessionID to get the lendingCompany details and the authorization token - QKEYS.AUTHORIZE
 * 3. Save the authorization token in state hook - authToken
 * 4. With the authorization token we got from step 2 above, make a POST request to the loanRequests endpoint to get a list of loans by the user - QKEYS.DRAFT_APPLICATION
 * 5. When we fetch the list of loans by the user, we filter to get the loan with status=DRAFT and we make another API call to get the details of the loan and prefill the application page with this information.
 * 6. While we are making a call to get the loans by the user, we make a call to the indexedBorrower endpoint to get the details of the authorized indexed borrower - QKEYS.INDEXED_BORROWER
 * 7. Using the defaultCurrency from step 1, we make an API call to QKEYS.COUNTRY_STATES to get the states in the country that has the defaultCurrency. Presently, we support only GHS and NGN
 *    Note: Step 6 is dependent on Step 7 and will only happen when step 6 is completed. When this is complete, we can redirect the user to the loan application page
 *
 */
